import { TemplateRef } from "@angular/core";
import { DataZoomComponentOption } from "echarts";
import { LineSeriesOption } from "echarts/charts";
import { DatasetComponentOption, GraphicComponentOption, GridComponentOption } from "echarts/components";
import * as echarts from "echarts/core";
import { Observable } from "rxjs";
import { IKpiSnapshot } from "@gtmhub/kpis";
import { IdMap } from "@gtmhub/util";
import { Comment } from "@webapp/comments";
import { KpiProjection } from "@webapp/kpis/models/kpi-projections.models";
import { Kpi, KpiTimeFilter } from "@webapp/kpis/models/kpis.models";
import { KpiCommentTargetType } from "../../kpi-comments/models";

export type ECOption = echarts.ComposeOption<LineSeriesOption | GridComponentOption | DatasetComponentOption | DataZoomComponentOption> & EGraphicComponentOption;
export type EGraphicComponentOption = echarts.ComposeOption<GraphicComponentOption>;
export type IKpiChartPoint = {
  date: string;
  snapshot: number;
  projection: number;
  projectionId?: string;
};

export type KpiChartData = {
  snapshots: IKpiSnapshot[];
  kpi: Kpi;
  projections: KpiProjection[];
  timeFilter: KpiTimeFilter;
  canEditKpi: boolean;
  canManageKpis: boolean;
  chartRefresh: void;
};

export type KpiDetailsState = {
  snapshots: IKpiSnapshot[];
  kpi: Kpi;
  projections: KpiProjection[];
  timeFilter: KpiTimeFilter;
};

export interface IKpiTooltipParams {
  snapshotPoint: IKpiTooltipPoint;
  projectionPoint: IKpiTooltipPoint;
}

interface IKpiTooltipPoint {
  value: number;
  date: string;
}

export interface IKpiChartStaticContext {
  snapshotsDateRange: {
    min: string;
    max: string;
  };
  projectionsDateRange: {
    min: string;
    max: string;
  };
  minPointOnInit: string;
  maxPointOnInit: string;
  chart: echarts.ECharts;
  timeFilter: KpiTimeFilter;
  kpi: Kpi;
  initialProjections: KpiProjection[];
}

export interface IKpiChartDynamicContext {
  chartPoints: IKpiChartPoint[];
  projections: KpiProjection[];
  projectionsLeadingCommentsMap: IdMap<string>; // key is projection date, value is earliest comment id
  snapshotsLeadingCommentsMap: IdMap<string>; // key is snapshot date, value is earliest comment id
  firstCommentContext: FirstCommentContext;
  openComment: Comment;
}

export interface IKpiChartTooltipContext {
  projections: KpiProjection[];
  kpi: Kpi;
}

export type FirstCommentContext = { inProgress: boolean; pointData?: KpiChartCommentData };

export type KpiChartPopoverOptions = {
  overlayClassName?: "without-arrow";
  placement: "bottom" | ["leftTop", "leftBottom", "rightTop", "rightBottom"];
  content: TemplateRef<void>;
};

export type KpiChartPointMenuItem = { label: string; handler(): void; isDestructive?: boolean; isVisible$: Observable<boolean>; dataTestId: string };

export type KpiChartCommentData = {
  targetType: KpiCommentTargetType;
  targetDate: string;
  targetValue: number;
};

/**
 * partial of MarkPointDataItemOption echarts type which is not exported
 */
export type KpiChartCommentMarkPointData = {
  /** commentId */
  name: string;
  x: number;
  y: number;
  /** label value used to store projectionId and is hidden via the label settings - show: false */
  value?: string;
};

/**
 * partial of MarkPointDataItemOption echarts type which is not exported
 */
type KpiChartCommentMarkPointStyle = {
  symbolSize: number;
  itemStyle: {
    color: string;
    borderColor: string;
  };
  /** the label is used to store projectionId but is not shown since we need it for business logic only */
  label: {
    show: boolean;
    color: string;
  };
};

/**
 * partial of MarkPointDataItemOption echarts type which is not exported
 */
export type KpiChartCommentMarkPoint = KpiChartCommentMarkPointData & KpiChartCommentMarkPointStyle;

export type KpiChartCommentsState = "default" | "adding_first_comment" | "any_comment_is_open";
export type KpiChartCommentStyle = "default" | "large" | "deemphasized";

export const projectionPinStylesMap: Map<KpiChartCommentStyle, KpiChartCommentMarkPointStyle> = new Map([
  [
    "default",
    {
      symbolSize: 30,
      itemStyle: {
        color: "#0057D7", // gtmds_color_blue-8
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
  [
    "large",
    {
      symbolSize: 40,
      itemStyle: {
        color: "#0057D7", // gtmds_color_blue-8
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
  [
    "deemphasized",
    {
      symbolSize: 30,
      itemStyle: {
        color: "#75A2F7", // gtmds_color_blue-5
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
]);

export const snapshotPinStylesMap: Map<KpiChartCommentStyle, KpiChartCommentMarkPointStyle> = new Map([
  [
    "default",
    {
      symbolSize: 30,
      itemStyle: {
        color: "#7E5FBF", // gtmds_color_purple-8
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
  [
    "large",
    {
      symbolSize: 40,
      itemStyle: {
        color: "#7E5FBF", // gtmds_color_purple-8
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
  [
    "deemphasized",
    {
      symbolSize: 30,
      itemStyle: {
        color: "#AC9EF2", // gtmds_color_purple-5
        borderColor: "white",
      },
      label: {
        show: false,
        color: "transparent",
      },
    },
  ],
]);
